/**
 * THE WORDS EVERY SCREEN USES, and no word that belongs to one screen.
 *
 * English is the origin and pt-BR is the translation — same rule and same shape
 * as `features/billing/i18n/*`. What qualifies for this file is a word whose
 * meaning does not change with the screen: a button that saves, a list that is
 * empty, a request that failed. "Salvar plano" is not one of those and belongs to
 * the feature; `action.save` is.
 *
 * ── WHAT IS DELIBERATELY NOT HERE ─────────────────────────────────────────
 *
 * Relative time ("3 hours ago", "yesterday"). `Intl.RelativeTimeFormat` already
 * says it in both languages, with the plural rules right, and hand-written
 * dictionary entries for it would be a worse translation maintained by us —
 * `shared/format` owns it.
 *
 * The server's refusals. Those are keyed by CODE and live in `./errors`, because
 * they answer a different question and the map is the contract with the API.
 */
export const commonMessages = {
	en: {
		chunk: {
			title: "This screen could not be loaded",
			hint: "The connection dropped, or the panel was updated while this tab was open. Reloading fetches the current version.",
			retry: "Reload",
		},
		action: {
			save: "Save",
			saving: "Saving…",
			saved: "Saved.",
			cancel: "Cancel",
			delete: "Delete",
			remove: "Remove",
			edit: "Edit",
			create: "Create",
			add: "Add",
			search: "Search",
			filter: "Filter",
			clear: "Clear",
			apply: "Apply",
			close: "Close",
			back: "Back",
			next: "Next",
			confirm: "Confirm",
			retry: "Try again",
			refresh: "Refresh",
			copy: "Copy",
			copied: "Copied.",
			send: "Send",
			open: "Open",
			upload: "Upload",
			download: "Download",
			select: "Select",
			yes: "Yes",
			no: "No",
			/** The link that opens the piece a page shows on the system page (`/system?no=`). */
			inSystem: "View in structure",
		},
		state: {
			loading: "Loading…",
			empty: "Nothing here yet.",
			/** The one an error card shows when nothing more precise is known. */
			error: "Something went wrong.",
			loadFailed: "Couldn't load this.",
			none: "None",
			all: "All",
			required: "Required",
			optional: "Optional",
			/** What a missing value looks like. Never `0` — a zero is a fact and a blank is a different fact. */
			absent: "—",
			unsaved: "Unsaved changes.",
		},
		/**
		 * What someone may do here, keyed by the SERVER's role code.
		 *
		 * These qualify for this file on the same test as `action.save`: "owner"
		 * means the same thing on the team roster, on your own account card and in
		 * an invitation, so it must not be spelled three times. It was: this file
		 * replaced a Portuguese-only `ROLE_LABEL` that existed in `team-display.ts`
		 * and again in `YourAccountCard.vue`.
		 *
		 * Keyed by code and not by index because the server owns the vocabulary. A
		 * role this map has not met shows its own code, which is readable, rather
		 * than an empty badge beside somebody's name.
		 */
		role: {
			owner: "Owner",
			admin: "Administrator",
			member: "Member",
		},
		/**
		 * The word for an agent whose NAME the screen does not have — the turn was
		 * recorded by a code (`builder`, `attendant`) and the agent list did not
		 * answer, or the agent is gone. Read by `shared/agent-display/agent-name`,
		 * which every consumption screen goes through.
		 */
		agentRole: {
			builder: "Ben",
			attendant: "attendant",
		},
		/**
		 * The country picker inside `PhoneField`.
		 *
		 * Here and not in a feature for the same reason as `action.save`: a phone is
		 * asked for on a contact form, a partner's details, the company card and every
		 * `phone` field a tenant invents this afternoon — one control, so one set of
		 * words. The country NAMES are not here: `Intl.DisplayNames` already knows
		 * them in every locale, and a hand-written list would be a worse translation
		 * maintained by us.
		 */
		phone: {
			pickCountry: "Choose the country",
			searchCountry: "Search country or code",
			noCountry: "No country found.",
		},
		/**
		 * O CAMPO EM SI — as palavras dos controles que o `TextField` desenha por
		 * dentro, e que nenhuma tela escreve.
		 *
		 * Grupo próprio e não uma linha em `phone`: era lá que as duas moravam, e
		 * `TextField.vue` pedia `field.showPassword` — um grupo que não existia.
		 * O vue-i18n não acha e devolve a chave, então o botão do olhinho anunciava
		 * "field.showPassword" a quem usa leitor de tela. Um rótulo que só existe
		 * para a leitura assistida é exatamente o que ninguém vê quebrar.
		 */
		field: {
			showPassword: "Show the password",
			hidePassword: "Hide the password",
			notFound: "Nothing found.",
		},
		/** O "mais" de um subtítulo comprido, numa linha só (`PageHeader`). */
		header: { more: "more", less: "less" },
		/** O grupo de uma tabela de dados sem grupo no menu — o mesmo no menu lateral e em /system. */
		menuGroup: { none: "Data" },
		/** Rodar uma automação a pedido (botão de tela, "Rodar agora"): `{name}` é o nome dela. */
		run: {
			done: "{name}: done. | {name}: done — {n} automations ran.",
			started: "{name}: started — it is waiting to continue.",
			nothing: "{name}: nothing to do here.",
			failed: "{name} failed: {reason}",
			failedUnknown: "{name} failed. Executions show why.",
		},
	},
	"pt-BR": {
		chunk: {
			title: "Não deu para carregar esta tela",
			hint: "A conexão caiu, ou o painel foi atualizado com esta aba aberta. Recarregar busca a versão atual.",
			retry: "Recarregar",
		},
		action: {
			save: "Salvar",
			saving: "Salvando…",
			saved: "Salvo.",
			cancel: "Cancelar",
			delete: "Apagar",
			remove: "Remover",
			edit: "Editar",
			create: "Criar",
			add: "Adicionar",
			search: "Buscar",
			filter: "Filtrar",
			clear: "Limpar",
			apply: "Aplicar",
			close: "Fechar",
			back: "Voltar",
			next: "Avançar",
			confirm: "Confirmar",
			retry: "Tentar de novo",
			refresh: "Atualizar",
			copy: "Copiar",
			copied: "Copiado.",
			send: "Enviar",
			open: "Abrir",
			upload: "Enviar arquivo",
			download: "Baixar",
			select: "Selecionar",
			yes: "Sim",
			no: "Não",
			inSystem: "Ver na estrutura",
		},
		state: {
			loading: "Carregando…",
			empty: "Ainda não há nada aqui.",
			error: "Alguma coisa deu errado.",
			loadFailed: "Não consegui carregar.",
			none: "Nenhum",
			all: "Todos",
			required: "Obrigatório",
			optional: "Opcional",
			absent: "—",
			unsaved: "Há mudanças não salvas.",
		},
		role: {
			owner: "Dono",
			admin: "Administrador",
			member: "Membro",
		},
		agentRole: {
			builder: "Ben",
			attendant: "atendente",
		},
		phone: {
			pickCountry: "Escolha o país",
			searchCountry: "Busque o país ou o código",
			noCountry: "Nenhum país encontrado.",
		},
		field: {
			showPassword: "Mostrar a senha",
			hidePassword: "Ocultar a senha",
			notFound: "Nada encontrado.",
		},
		/** O "mais" de um subtítulo comprido, numa linha só (`PageHeader`). */
		header: { more: "mais", less: "menos" },
		menuGroup: { none: "Dados" },
		/** Rodar uma automação a pedido (botão de tela, "Rodar agora"): `{name}` é o nome dela. */
		run: {
			done: "{name}: feito. | {name}: feito — {n} automações rodaram.",
			started: "{name}: começou — está esperando para continuar.",
			nothing: "{name}: nada a fazer aqui.",
			failed: "{name} falhou: {reason}",
			failedUnknown: "{name} falhou. As Execuções mostram o porquê.",
		},
	},
	el: {
		chunk: {
			title: "Δεν φορτώθηκε αυτή η οθόνη",
			hint: "Η σύνδεση χάθηκε, ή ο πίνακας ενημερώθηκε με ανοιχτή αυτή την καρτέλα. Η επαναφόρτωση φέρνει την τρέχουσα έκδοση.",
			retry: "Επαναφόρτωση",
		},
		action: {
			save: "Αποθήκευση",
			saving: "Αποθήκευση…",
			saved: "Αποθηκεύτηκε.",
			cancel: "Ακύρωση",
			delete: "Διαγραφή",
			remove: "Αφαίρεση",
			edit: "Επεξεργασία",
			create: "Δημιουργία",
			add: "Προσθήκη",
			search: "Αναζήτηση",
			filter: "Φίλτρο",
			clear: "Καθαρισμός",
			apply: "Εφαρμογή",
			close: "Κλείσιμο",
			back: "Πίσω",
			next: "Επόμενο",
			confirm: "Επιβεβαίωση",
			retry: "Νέα προσπάθεια",
			refresh: "Ανανέωση",
			copy: "Αντιγραφή",
			copied: "Αντιγράφηκε.",
			send: "Αποστολή",
			open: "Άνοιγμα",
			upload: "Μεταφόρτωση",
			download: "Λήψη",
			select: "Επιλογή",
			yes: "Ναι",
			no: "Όχι",
			inSystem: "Προβολή στη δομή",
		},
		state: {
			loading: "Φόρτωση…",
			empty: "Δεν υπάρχει τίποτα ακόμη.",
			error: "Κάτι πήγε στραβά.",
			loadFailed: "Δεν μπόρεσα να φορτώσω.",
			none: "Κανένα",
			all: "Όλα",
			required: "Υποχρεωτικό",
			optional: "Προαιρετικό",
			absent: "—",
			unsaved: "Υπάρχουν μη αποθηκευμένες αλλαγές.",
		},
		role: {
			owner: "Ιδιοκτήτης",
			admin: "Διαχειριστής",
			member: "Μέλος",
		},
		agentRole: {
			builder: "Ben",
			attendant: "εκπρόσωπος εξυπηρέτησης",
		},
		phone: {
			pickCountry: "Διάλεξε τη χώρα",
			searchCountry: "Αναζήτησε χώρα ή κωδικό",
			noCountry: "Δεν βρέθηκε χώρα.",
		},
		field: {
			showPassword: "Εμφάνιση του κωδικού",
			hidePassword: "Απόκρυψη του κωδικού",
			notFound: "Δεν βρέθηκε τίποτα.",
		},
		/** O "mais" de um subtítulo comprido, numa linha só (`PageHeader`). */
		header: { more: "περισσότερα", less: "λιγότερα" },
		menuGroup: { none: "Δεδομένα" },
		/** Rodar uma automação a pedido (botão de tela, "Rodar agora"): `{name}` é o nome dela. */
		run: {
			done: "{name}: έγινε. | {name}: έγινε — εκτελέστηκαν {n} αυτοματισμοί.",
			started: "{name}: ξεκίνησε — περιμένει για να συνεχίσει.",
			nothing: "{name}: τίποτα να γίνει εδώ.",
			failed: "{name} απέτυχε: {reason}",
			failedUnknown: "{name} απέτυχε. Οι Εκτελέσεις δείχνουν γιατί.",
		},
	},
	es: {
		chunk: {
			title: "No se pudo cargar esta pantalla",
			hint: "Se cayó la conexión, o el panel se actualizó con esta pestaña abierta. Recargar trae la versión actual.",
			retry: "Recargar",
		},
		action: {
			save: "Guardar",
			saving: "Guardando…",
			saved: "Guardado.",
			cancel: "Cancelar",
			delete: "Eliminar",
			remove: "Quitar",
			edit: "Editar",
			create: "Crear",
			add: "Añadir",
			search: "Buscar",
			filter: "Filtrar",
			clear: "Limpiar",
			apply: "Aplicar",
			close: "Cerrar",
			back: "Atrás",
			next: "Siguiente",
			confirm: "Confirmar",
			retry: "Reintentar",
			refresh: "Actualizar",
			copy: "Copiar",
			copied: "Copiado.",
			send: "Enviar",
			open: "Abrir",
			upload: "Subir",
			download: "Descargar",
			select: "Seleccionar",
			yes: "Sí",
			no: "No",
			inSystem: "Ver en la estructura",
		},
		state: {
			loading: "Cargando…",
			empty: "Aquí todavía no hay nada.",
			error: "Algo ha ido mal.",
			loadFailed: "No pude cargar esto.",
			none: "Ninguno",
			all: "Todos",
			required: "Obligatorio",
			optional: "Opcional",
			absent: "—",
			unsaved: "Hay cambios sin guardar.",
		},
		role: {
			owner: "Propietario",
			admin: "Administrador",
			member: "Miembro",
		},
		agentRole: {
			builder: "Ben",
			attendant: "agente de atención",
		},
		phone: {
			pickCountry: "Elige el país",
			searchCountry: "Busca el país o el código",
			noCountry: "Ningún país encontrado.",
		},
		field: {
			showPassword: "Mostrar la contraseña",
			hidePassword: "Ocultar la contraseña",
			notFound: "No se encontró nada.",
		},
		/** O "mais" de um subtítulo comprido, numa linha só (`PageHeader`). */
		header: { more: "más", less: "menos" },
		menuGroup: { none: "Datos" },
		/** Rodar uma automação a pedido (botão de tela, "Rodar agora"): `{name}` é o nome dela. */
		run: {
			done: "{name}: hecho. | {name}: hecho — se ejecutaron {n} automatizaciones.",
			started: "{name}: empezó — está esperando para continuar.",
			nothing: "{name}: nada que hacer aquí.",
			failed: "{name} falló: {reason}",
			failedUnknown: "{name} falló. Las Ejecuciones muestran por qué.",
		},
	},
	fr: {
		chunk: {
			title: "Impossible de charger cet écran",
			hint: "La connexion a été perdue, ou le panneau a été mis à jour avec cet onglet ouvert. Recharger récupère la version actuelle.",
			retry: "Recharger",
		},
		action: {
			save: "Enregistrer",
			saving: "Enregistrement…",
			saved: "Enregistré.",
			cancel: "Annuler",
			delete: "Supprimer",
			remove: "Retirer",
			edit: "Modifier",
			create: "Créer",
			add: "Ajouter",
			search: "Rechercher",
			filter: "Filtrer",
			clear: "Effacer",
			apply: "Appliquer",
			close: "Fermer",
			back: "Retour",
			next: "Suivant",
			confirm: "Confirmer",
			retry: "Réessayer",
			refresh: "Actualiser",
			copy: "Copier",
			copied: "Copié.",
			send: "Envoyer",
			open: "Ouvrir",
			upload: "Téléverser",
			download: "Télécharger",
			select: "Sélectionner",
			yes: "Oui",
			no: "Non",
			inSystem: "Voir dans la structure",
		},
		state: {
			loading: "Chargement…",
			empty: "Rien ici pour l'instant.",
			error: "Quelque chose a échoué.",
			loadFailed: "Impossible de charger.",
			none: "Aucun",
			all: "Tous",
			required: "Obligatoire",
			optional: "Facultatif",
			absent: "—",
			unsaved: "Des modifications ne sont pas enregistrées.",
		},
		role: {
			owner: "Propriétaire",
			admin: "Administrateur",
			member: "Membre",
		},
		agentRole: {
			builder: "Ben",
			attendant: "agent d'accueil",
		},
		phone: {
			pickCountry: "Choisissez le pays",
			searchCountry: "Cherchez le pays ou l'indicatif",
			noCountry: "Aucun pays trouvé.",
		},
		field: {
			showPassword: "Afficher le mot de passe",
			hidePassword: "Masquer le mot de passe",
			notFound: "Aucun résultat.",
		},
		/** O "mais" de um subtítulo comprido, numa linha só (`PageHeader`). */
		header: { more: "plus", less: "moins" },
		menuGroup: { none: "Données" },
		/** Rodar uma automação a pedido (botão de tela, "Rodar agora"): `{name}` é o nome dela. */
		run: {
			done: "{name} : fait. | {name} : fait — {n} automatisations exécutées.",
			started: "{name} : démarré — en attente pour continuer.",
			nothing: "{name} : rien à faire ici.",
			failed: "{name} a échoué : {reason}",
			failedUnknown: "{name} a échoué. Les Exécutions montrent pourquoi.",
		},
	},
	it: {
		chunk: {
			title: "Non è stato possibile caricare questa schermata",
			hint: "La connessione è caduta, o il pannello è stato aggiornato con questa scheda aperta. Ricaricare porta la versione attuale.",
			retry: "Ricarica",
		},
		action: {
			save: "Salva",
			saving: "Salvataggio…",
			saved: "Salvato.",
			cancel: "Annulla",
			delete: "Elimina",
			remove: "Rimuovi",
			edit: "Modifica",
			create: "Crea",
			add: "Aggiungi",
			search: "Cerca",
			filter: "Filtra",
			clear: "Pulisci",
			apply: "Applica",
			close: "Chiudi",
			back: "Indietro",
			next: "Avanti",
			confirm: "Conferma",
			retry: "Riprova",
			refresh: "Aggiorna",
			copy: "Copia",
			copied: "Copiato.",
			send: "Invia",
			open: "Apri",
			upload: "Carica",
			download: "Scarica",
			select: "Seleziona",
			yes: "Sì",
			no: "No",
			inSystem: "Vedi nella struttura",
		},
		state: {
			loading: "Caricamento…",
			empty: "Qui non c'è ancora nulla.",
			error: "Qualcosa è andato storto.",
			loadFailed: "Non sono riuscito a caricare.",
			none: "Nessuno",
			all: "Tutti",
			required: "Obbligatorio",
			optional: "Facoltativo",
			absent: "—",
			unsaved: "Ci sono modifiche non salvate.",
		},
		role: {
			owner: "Proprietario",
			admin: "Amministratore",
			member: "Membro",
		},
		agentRole: {
			builder: "Ben",
			attendant: "addetto all'assistenza",
		},
		phone: {
			pickCountry: "Scegli il paese",
			searchCountry: "Cerca il paese o il prefisso",
			noCountry: "Nessun paese trovato.",
		},
		field: {
			showPassword: "Mostra la password",
			hidePassword: "Nascondi la password",
			notFound: "Nessun risultato.",
		},
		/** O "mais" de um subtítulo comprido, numa linha só (`PageHeader`). */
		header: { more: "altro", less: "meno" },
		menuGroup: { none: "Dati" },
		/** Rodar uma automação a pedido (botão de tela, "Rodar agora"): `{name}` é o nome dela. */
		run: {
			done: "{name}: fatto. | {name}: fatto — {n} automazioni eseguite.",
			started: "{name}: avviato — è in attesa per continuare.",
			nothing: "{name}: niente da fare qui.",
			failed: "{name} non è riuscito: {reason}",
			failedUnknown: "{name} non è riuscito. Le Esecuzioni mostrano perché.",
		},
	},
	de: {
		chunk: {
			title: "Dieser Bildschirm konnte nicht geladen werden",
			hint: "Die Verbindung brach ab, oder das Panel wurde aktualisiert, während dieser Tab offen war. Neu laden holt die aktuelle Version.",
			retry: "Neu laden",
		},
		action: {
			save: "Speichern",
			saving: "Wird gespeichert…",
			saved: "Gespeichert.",
			cancel: "Abbrechen",
			delete: "Löschen",
			remove: "Entfernen",
			edit: "Bearbeiten",
			create: "Anlegen",
			add: "Hinzufügen",
			search: "Suchen",
			filter: "Filtern",
			clear: "Leeren",
			apply: "Anwenden",
			close: "Schließen",
			back: "Zurück",
			next: "Weiter",
			confirm: "Bestätigen",
			retry: "Noch einmal versuchen",
			refresh: "Aktualisieren",
			copy: "Kopieren",
			copied: "Kopiert.",
			send: "Senden",
			open: "Öffnen",
			upload: "Hochladen",
			download: "Herunterladen",
			select: "Auswählen",
			yes: "Ja",
			no: "Nein",
			inSystem: "In der Struktur ansehen",
		},
		state: {
			loading: "Wird geladen…",
			empty: "Hier ist noch nichts.",
			error: "Da ist etwas schiefgegangen.",
			loadFailed: "Konnte nicht geladen werden.",
			none: "Keine",
			all: "Alle",
			required: "Pflicht",
			optional: "Optional",
			absent: "—",
			unsaved: "Es gibt ungespeicherte Änderungen.",
		},
		role: {
			owner: "Inhaber",
			admin: "Administrator",
			member: "Mitglied",
		},
		agentRole: {
			builder: "Ben",
			attendant: "Kundenbetreuung",
		},
		phone: {
			pickCountry: "Wähle das Land",
			searchCountry: "Land oder Vorwahl suchen",
			noCountry: "Kein Land gefunden.",
		},
		field: {
			showPassword: "Passwort anzeigen",
			hidePassword: "Passwort verbergen",
			notFound: "Nichts gefunden.",
		},
		/** O "mais" de um subtítulo comprido, numa linha só (`PageHeader`). */
		header: { more: "mehr", less: "weniger" },
		menuGroup: { none: "Daten" },
		/** Rodar uma automação a pedido (botão de tela, "Rodar agora"): `{name}` é o nome dela. */
		run: {
			done: "{name}: erledigt. | {name}: erledigt — {n} Automationen liefen.",
			started: "{name}: gestartet — wartet darauf, weiterzumachen.",
			nothing: "{name}: hier gibt es nichts zu tun.",
			failed: "{name} ist fehlgeschlagen: {reason}",
			failedUnknown: "{name} ist fehlgeschlagen. Die Ausführungen zeigen, warum.",
		},
	},
} as const;
